import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Link, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { supabase } from "../../../src/integrations/supabase/client";
import { useToast } from "../hooks/use-toast";
import EnhancedInteractiveMap from "@/components/EnhancedInteractiveMap";
import { motion } from "framer-motion";
import { MapPin, Users, Calendar, Video, Clock, Phone, Mail, BookOpen, Archive } from "lucide-react";

interface BibleStudy {
  city: string;
  type: string;
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
}

interface Region {
  id: string;
  name: string;
  states: string[];
  contact_email?: string;
  contact_phone?: string;
  description?: string;
  coordinator_name?: string;
  coordinator_email?: string;
  coordinator_phone?: string;
  universities?: number;
  total_members?: number;
  active_prayer_cells?: number;
  color?: string;
  bible_studies?: BibleStudy[];
}

interface Conference {
  id: string;
  region_id: string;
  conference_name: string;
  description?: string;
  slogan?: string;
  conference_date?: string;
  conference_time?: string;
  location?: string;
  facilitators?: any[];
  facilitator_name?: string;
  facilitator_email?: string;
  flyer_url?: string;
  registration_url?: string;
  status: string;
}

interface Activity {
  id: string;
  region_id: string;
  title: string;
  description?: string;
  activity_type: string;
  schedule?: string;
  zoom_link?: string;
  meeting_id?: string;
  passcode?: string;
  facilitators?: any[];
  is_active: boolean;
}

const Regions = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [venuesByRegion, setVenuesByRegion] = useState<Record<string, any>>({});
  const [activities, setActivities] = useState<Activity[]>([]);
  const [universities, setUniversities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const { toast } = useToast();
  const location = useLocation();

  useEffect(() => {
    fetchRegions();
    fetchConferences();
    fetchVenues();
    fetchActivities();
    fetchUniversities();
  }, []);

  // Scroll to regions section if hash is present
  useEffect(() => {
    if (location.hash === "#regions") {
      const element = document.getElementById("regions");
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  const fetchRegions = async () => {
    try {
      const [regionsRes, coordinatorsRes] = await Promise.all([
        supabase.from("regions").select("*").order("name"),
        // Get coordinators from member_roles table to support multiple roles
        supabase
          .from("member_roles")
          .select(
            `
            role,
            member_id,
            members!inner(
              id,
              full_name,
              email,
              phone,
              profile_image_url,
              region_id,
              is_active,
              regions!inner(id, name)
            )
          `,
          )
          .in("role", [
            "regional_president",
            "regional_vice_president",
            "regional_secretary",
            "regional_joint_secretary",
            "regional_treasurer",
          ])
          .eq("members.is_active", true),
      ]);

      if (regionsRes.error) throw regionsRes.error;
      if (coordinatorsRes.error) throw coordinatorsRes.error;

      const regionsData = regionsRes.data || [];

      // Group coordinators by region
      const coordsByRegion: Record<string, any[]> = {};
      coordinatorsRes.data?.forEach((item) => {
        const member = item.members;
        if (member.region_id) {
          if (!coordsByRegion[member.region_id]) {
            coordsByRegion[member.region_id] = [];
          }
          coordsByRegion[member.region_id].push({
            ...member,
            role: item.role,
          });
        }
      });

      // Merge coordinator data with regions
      const regionsWithCoordinators = regionsData.map((region) => {
        const regionCoordinators = coordsByRegion[region.id] || [];

        // Role priority for sorting
        const rolePriorityMap: Record<string, number> = {
          regional_president: 1,
          regional_vice_president: 2,
          regional_secretary: 3,
          regional_joint_secretary: 4,
          regional_treasurer: 5,
        };

        // Sort coordinators by role priority
        const sortedCoordinators = regionCoordinators.sort(
          (a: any, b: any) => (rolePriorityMap[a.role] || 999) - (rolePriorityMap[b.role] || 999),
        );

        const primaryCoordinator = sortedCoordinators[0];

        // Parse bible_studies if they are JSON strings
        let parsedBibleStudies = region.bible_studies || [];
        if (Array.isArray(parsedBibleStudies)) {
          parsedBibleStudies = parsedBibleStudies
            .map((study: any) => {
              if (typeof study === "string") {
                try {
                  return JSON.parse(study);
                } catch {
                  return null;
                }
              }
              return study;
            })
            .filter(Boolean);
        }

        return {
          ...region,
          bible_studies: parsedBibleStudies,
          coordinator_name: primaryCoordinator?.full_name || "",
          coordinator_email: primaryCoordinator?.email || "",
          coordinator_phone: primaryCoordinator?.phone || "",
          coordinators: sortedCoordinators,
        };
      });

      setRegions(regionsWithCoordinators);
      if (regionsWithCoordinators.length > 0) {
        setSelectedRegion(regionsWithCoordinators[0].id);
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      toast({
        title: "Error",
        description: "Failed to fetch regions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchConferences = async () => {
    try {
      const [{ data, error }, { data: kits }] = await Promise.all([
        supabase
          .from("conferences")
          .select("*")
          .eq("is_active", true)
          .order("conference_date", { ascending: false }),
        supabase
          .from("conference_kit_events" as any)
          .select("region_id, venue_name, venue_address, city, state, start_date, end_date, conference_year")
          .eq("conference_year", 2026),
      ]);

      if (error) throw error;
      // Index kit events by region_id (prefer earliest start_date)
      const kitByRegion: Record<string, any> = {};
      (kits || []).forEach((k: any) => {
        if (!k.region_id) return;
        const existing = kitByRegion[k.region_id];
        if (!existing || (k.start_date && (!existing.start_date || k.start_date < existing.start_date))) {
          kitByRegion[k.region_id] = k;
        }
      });
      // Merge kit event details into matching upcoming conferences
      const merged = (data || []).map((c: any) => {
        const k = kitByRegion[c.region_id];
        if (!k) return c;
        const looksLikeTbd = !c.location || /to be announced|tbd|\[/i.test(c.location);
        const venueLine = [k.venue_name, k.city && k.state ? `${k.city}, ${k.state}` : (k.city || k.state)]
          .filter(Boolean).join(" — ");
        return {
          ...c,
          location: looksLikeTbd && venueLine ? venueLine : c.location,
          conference_date: c.conference_date || k.start_date || null,
        };
      });
      setConferences(merged);
    } catch (error) {
      console.error("Error fetching conferences:", error);
    }
  };

  const fetchVenues = async () => {
    try {
      const { data, error } = await (supabase.from("conference_content" as any) as any)
        .select("*")
        .eq("conference_slug", "regional-conference-2026")
        .eq("section", "venue")
        .eq("is_active", true);
      if (error) throw error;
      const map: Record<string, any> = {};
      (data || []).forEach((v: any) => { if (v.region_id) map[v.region_id] = v; });
      setVenuesByRegion(map);
    } catch (e) {
      console.error("Error fetching venues:", e);
    }
  };

  const fetchActivities = async () => {
    try {
      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error("Error fetching activities:", error);
    }
  };

  const fetchUniversities = async () => {
    try {
      const { data, error } = await supabase.from("universities").select("*").order("name");

      if (error) throw error;
      setUniversities(data || []);
    } catch (error) {
      console.error("Error fetching universities:", error);
    }
  };

  const getRegionConferences = (regionId: string) => {
    return conferences.filter((conf) => conf.region_id === regionId);
  };

  const isUpcoming = (c: Conference) => {
    if ((c as any).status === "upcoming") return true;
    if ((c as any).status === "completed") return false;
    if (c.conference_date) return new Date(c.conference_date) >= new Date();
    return false;
  };
  const getLatestConferences = (regionId: string) =>
    getRegionConferences(regionId).filter(isUpcoming);
  const getPastConferences = (regionId: string) =>
    getRegionConferences(regionId).filter((c) => !isUpcoming(c));

  const getRegionActivities = (regionId: string) => {
    return activities.filter((act) => act.region_id === regionId);
  };

  const getRegionUniversities = (regionId: string) => {
    return universities.filter((uni) => uni.region_id === regionId);
  };

  const getCurrentRegion = () => {
    return regions.find((r) => r.id === selectedRegion);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#ecf5fe]">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto text-white space-y-6">
            <h1 className="text-4xl lg:text-6xl font-bold">Find Your Region</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Connect with fellow students and ministry leaders in your area across 6 regions nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/3 to-primary/5"></div>
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <EnhancedInteractiveMap
              showStatsHeader={true}
              title="Connecting Students Across United States of America"
              description="Explore our active regions and discover fellowship opportunities near you"
            />
          </motion.div>
        </div>
      </section>

      {/* Regional Tabs Section */}
      <section id="regions" className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <Tabs value={selectedRegion} onValueChange={setSelectedRegion} className="space-y-8">
            <div className="overflow-x-auto -mx-4 px-4 pb-2">
              <TabsList className="inline-flex min-w-full lg:grid lg:w-full lg:grid-cols-6 gap-2 p-1">
                {regions.map((region) => {
                  const isActive = selectedRegion === region.id;
                  const regionColor = region.color || "#8B5CF6";

                  return (
                    <TabsTrigger
                      key={region.id}
                      value={region.id}
                      className="text-sm whitespace-nowrap relative transition-all duration-200 data-[state=active]:shadow-md"
                      style={
                        isActive
                          ? {
                              backgroundColor: regionColor + "15",
                              color: regionColor,
                              borderBottom: `3px solid ${regionColor}`,
                            }
                          : {
                              borderBottom: `2px solid transparent`,
                            }
                      }
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = regionColor + "08";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "";
                        }
                      }}
                    >
                      {region.name.replace(" Region", "")}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

            {regions.map((region) => (
              <TabsContent key={region.id} value={region.id} className="space-y-8">
                {/* Latest Conference (moved above Region Info Card) */}
                <div className="space-y-10">
                  <div>
                    <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                      <Calendar className="w-6 h-6 text-primary" />
                      Latest Conference
                    </h3>
                    {getLatestConferences(region.id).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {getLatestConferences(region.id).map((conference, index) => {
                          const venue = venuesByRegion[region.id];
                          return (
                            <motion.div
                              key={conference.id}
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                              whileHover={{ y: -6 }}
                            >
                              <Card className="divine-card h-full border-2 border-primary/30">
                                <CardContent className="space-y-4 pt-6">
                                  <div className="inline-block text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                                    Upcoming
                                  </div>
                                  <div className="font-semibold text-foreground text-xl">{conference.conference_name}</div>
                                  {conference.slogan && (
                                    <p className="text-sm italic text-muted-foreground">{conference.slogan}</p>
                                  )}
                                  <div className="space-y-2 text-sm text-muted-foreground">
                                    {(venue?.title || conference.location) && (
                                      <div className="flex items-start gap-2">
                                        <MapPin className="w-4 h-4 text-ministry-emerald mt-0.5 flex-shrink-0" />
                                        <div>
                                          <div className="font-medium text-foreground">{venue?.title || conference.location}</div>
                                          {venue?.data?.address_line1 && (
                                            <div className="text-xs">{venue.data.address_line1}{venue.data.address_line2 ? `, ${venue.data.address_line2}` : ""}</div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {conference.conference_date && (
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-ministry-gold flex-shrink-0" />
                                        <span>{new Date(conference.conference_date).toLocaleDateString()}</span>
                                      </div>
                                    )}
                                  </div>
                                  <Button asChild className="w-full">
                                    <Link to={`/conference/${(region.name || "").toLowerCase().replace(/region/g, "").replace(/[^a-z0-9]+/g, "").trim()}`}>Register Now</Link>
                                  </Button>
                                </CardContent>
                              </Card>
                            </motion.div>
                          );
                        })}
                      </div>
                    ) : (
                      <Card className="bg-muted/30">
                        <CardContent className="py-8 text-center">
                          <p className="text-muted-foreground">No upcoming conferences yet.</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {getPastConferences(region.id).length > 0 && (
                    <div>
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-muted-foreground">
                        <Archive className="w-5 h-5" />
                        Past Conferences (Archive)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getPastConferences(region.id).map((conference) => (
                          <Card key={conference.id} className="bg-muted/20 opacity-90">
                            <CardContent className="space-y-2 pt-5">
                              <div className="font-medium text-sm">{conference.conference_name}</div>
                              {conference.conference_date && (
                                <div className="text-xs text-muted-foreground">
                                  {new Date(conference.conference_date).toLocaleDateString()}
                                </div>
                              )}
                              {conference.location && (
                                <div className="text-xs text-muted-foreground">{conference.location}</div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Region Info Card */}
                <Card className="divine-card hover:shadow-lg transition-all duration-300 overflow-hidden">
                  <div className="h-2 w-full" style={{ background: region.color || "hsl(var(--primary))" }} />
                  <CardHeader className="bg-gradient-to-br from-primary/5 to-secondary/5">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div>
                        <CardTitle className="text-3xl mb-2">{region.name}</CardTitle>
                        <p className="text-muted-foreground">{region.description}</p>
                      </div>
                      {region.coordinator_name ? (
                        <div className="flex items-center gap-3 bg-white dark:bg-card p-4 rounded-lg shadow-sm">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-primary text-white">
                              {region.coordinator_name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="text-sm font-semibold">{region.coordinator_name}</div>
                            <div className="text-xs text-muted-foreground">Regional Coordinator</div>
                            {region.coordinator_email && (
                              <div className="text-xs text-primary">{region.coordinator_email}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Regional coordinator not assigned yet</p>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {/* Regional Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-ministry-emerald/10 to-ministry-cyan/10 p-4 rounded-lg border border-ministry-emerald/20">
                        <div className="text-2xl font-bold text-ministry-emerald">{region.universities || 0}</div>
                        <div className="text-sm text-muted-foreground">Universities</div>
                      </div>
                      <div className="bg-gradient-to-br from-ministry-gold/10 to-ministry-rose/10 p-4 rounded-lg border border-ministry-gold/20">
                        <div className="text-2xl font-bold text-ministry-gold">{region.total_members || 0}</div>
                        <div className="text-sm text-muted-foreground">Members</div>
                      </div>
                      <div className="bg-gradient-to-br from-ministry-blue/10 to-ministry-cyan/10 p-4 rounded-lg border border-ministry-blue/20">
                        <div className="text-2xl font-bold text-ministry-blue">{region.active_prayer_cells || 0}</div>
                        <div className="text-sm text-muted-foreground">Prayer Cells</div>
                      </div>
                      <div className="bg-gradient-to-br from-ministry-rose/10 to-ministry-gold/10 p-4 rounded-lg border border-ministry-rose/20">
                        <div className="text-2xl font-bold text-ministry-rose">{region.states.length}</div>
                        <div className="text-sm text-muted-foreground">States</div>
                      </div>
                    </div>

                    {/* States Covered */}
                    <div
                      className="mb-6 pt-6 border-t-4"
                      style={{ borderTopColor: region.color || "hsl(var(--primary))" }}
                    >
                      <h4
                        className="font-semibold mb-3 flex items-center gap-2"
                        style={{ color: region.color || "hsl(var(--primary))" }}
                      >
                        <MapPin className="h-5 w-5" style={{ color: region.color || "hsl(var(--primary))" }} />
                        States Covered
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {region.states.map((state) => (
                          <Badge
                            key={state}
                            variant="outline"
                            className="px-3 py-1"
                            style={{
                              borderColor: region.color || "hsl(var(--primary))",
                              color: region.color || "hsl(var(--primary))",
                            }}
                          >
                            {state}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Bible Studies Section */}
                    {region.bible_studies && region.bible_studies.length > 0 && (
                      <div
                        className="mb-6 pt-6 border-t-4"
                        style={{ borderTopColor: region.color || "hsl(var(--primary))" }}
                      >
                        <h4
                          className="font-semibold mb-3 flex items-center gap-2"
                          style={{ color: region.color || "hsl(var(--primary))" }}
                        >
                          <BookOpen className="h-5 w-5" style={{ color: region.color || "hsl(var(--primary))" }} />
                          Bible Studies & Fellowships
                        </h4>
                        <div className="grid gap-4 md:grid-cols-2">
                          {region.bible_studies.map((study, idx) => (
                            <Card key={idx} className="overflow-hidden">
                              <div
                                className="h-1 w-full"
                                style={{ background: region.color || "hsl(var(--primary))" }}
                              />
                              <CardContent className="pt-4">
                                <div className="space-y-2">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                    <span className="font-semibold text-sm">{study.city}</span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-muted-foreground">{study.type}</span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                    <span className="text-sm font-medium">{study.contact_name}</span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Phone className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                    <a
                                      href={`tel:${study.contact_phone}`}
                                      className="text-sm text-primary hover:underline"
                                    >
                                      {study.contact_phone}
                                    </a>
                                  </div>
                                  {study.contact_email && (
                                    <div className="flex items-start gap-2">
                                      <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <a
                                        href={`mailto:${study.contact_email}`}
                                        className="text-sm text-primary hover:underline"
                                      >
                                        {study.contact_email}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Universities Section */}
                    {getRegionUniversities(region.id).length > 0 && (
                      <div
                        className="mb-6 pt-6 border-t-4"
                        style={{ borderTopColor: region.color || "hsl(var(--primary))" }}
                      >
                        <h4
                          className="font-semibold mb-3 flex items-center gap-2"
                          style={{ color: region.color || "hsl(var(--primary))" }}
                        >
                          <Users className="h-5 w-5" style={{ color: region.color || "hsl(var(--primary))" }} />
                          Universities
                        </h4>
                        <div className="grid gap-4 md:grid-cols-2">
                          {getRegionUniversities(region.id).map((university) => (
                            <Card key={university.id} className="overflow-hidden">
                              <div
                                className="h-1 w-full"
                                style={{ background: region.color || "hsl(var(--primary))" }}
                              />
                              <CardContent className="pt-4">
                                <div className="space-y-2">
                                  <h5 className="font-semibold text-base">{university.name}</h5>
                                  {university.city && university.state && (
                                    <div className="flex items-start gap-2">
                                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <span className="text-sm text-muted-foreground">
                                        {university.city}, {university.state}
                                      </span>
                                    </div>
                                  )}
                                  {university.contact_person && (
                                    <div className="flex items-start gap-2">
                                      <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <span className="text-sm font-medium">{university.contact_person}</span>
                                    </div>
                                  )}
                                  {university.contact_phone && (
                                    <div className="flex items-start gap-2">
                                      <Phone className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <a
                                        href={`tel:${university.contact_phone}`}
                                        className="text-sm text-primary hover:underline"
                                      >
                                        {university.contact_phone}
                                      </a>
                                    </div>
                                  )}
                                  {university.contact_email && (
                                    <div className="flex items-start gap-2">
                                      <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <a
                                        href={`mailto:${university.contact_email}`}
                                        className="text-sm text-primary hover:underline"
                                      >
                                        {university.contact_email}
                                      </a>
                                    </div>
                                  )}
                                  {university.active_members > 0 && (
                                    <div className="flex items-start gap-2">
                                      <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <span className="text-sm text-muted-foreground">
                                        {university.active_members} Active Members
                                      </span>
                                    </div>
                                  )}
                                  {university.has_fellowship && university.fellowship_meeting_time && (
                                    <div className="flex items-start gap-2">
                                      <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <span className="text-sm text-muted-foreground">
                                        Fellowship: {university.fellowship_meeting_time}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Activities Section */}
                <div>
                  <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                    <Video className="w-6 h-6 text-primary" />
                    Activities & Meetings
                  </h3>
                  {getRegionActivities(region.id).length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {getRegionActivities(region.id).map((activity) => (
                        <Card key={activity.id} className="elegant-shadow hover:shadow-lg transition-shadow">
                          <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Clock className="w-5 h-5 text-primary" />
                              {activity.title}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {activity.description && (
                              <p className="text-sm text-muted-foreground">{activity.description}</p>
                            )}
                            <div className="space-y-2 text-sm">
                              {activity.schedule && (
                                <div>
                                  <span className="font-semibold">Schedule:</span> {activity.schedule}
                                </div>
                              )}
                              {activity.meeting_id && (
                                <div>
                                  <span className="font-semibold">Meeting ID:</span> {activity.meeting_id}
                                </div>
                              )}
                              {activity.passcode && (
                                <div>
                                  <span className="font-semibold">Passcode:</span> {activity.passcode}
                                </div>
                              )}
                            </div>
                            {activity.zoom_link && (
                              <Button asChild className="w-full ministry-gradient mt-4">
                                <a href={activity.zoom_link} target="_blank" rel="noopener noreferrer">
                                  Join Zoom Meeting
                                </a>
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="bg-muted/30">
                      <CardContent className="py-8 text-center">
                        <p className="text-muted-foreground">No activities scheduled at this time. Check back soon!</p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Latest Conference moved above */}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-10 bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto text-white space-y-6">
            <h2 className="text-3xl lg:text-4xl font-bold">Ready to Get Connected?</h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              Don't wait! Reach out to your regional coordinator today and become part of a thriving Christian community
              on your campus.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2"
              >
                <Link to="/new-students">New Student Guide</Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2"
              >
                <Link to="/prayer">Join Prayer Map</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Regions;
